/**
 * @author    yewon@zumper.com (Yewon Lee)
 * @copyright Copyright (c) 2016, Padmapper
 */

import { isPadMapper } from '@zcode/env'
import { getUserAgent, isCypress, isNumber } from '@zcode/utils'
import { logger } from '../../logger'
import * as GridC from '../constants/grid'
import { ON_BROWSER } from '../env'
import { oforEach } from './oforEach'
import { omap, omapFiltered } from './omap'
export { correctSpace } from './correctSpace'
export { oforEach, omap, omapFiltered }

export const isCookieEnabled = ON_BROWSER && window.navigator && window.navigator.cookieEnabled

/**
 * @param  {string} [userAgent] navigator userAgent
 * @returns {boolean} true if userAgent matches ios strings
 */
export const isIOS = (userAgent) => {
  const ua = userAgent || getUserAgent()
  return ua ? /(iPad|iPhone|iPod)/i.test(ua) : false
}

// Very specific detector to handle the botto notch that exists in iPhone X.
// TODO: rename once other phones that have the same bottom notch or
//       remove when we come up with a new design.
export const isIPhoneX = () => {
  if (ON_BROWSER && isIOS()) {
    // Get the device pixel ratio
    const ratio = window.devicePixelRatio || 1
    // iPhone X Detection, use pixelRatio to define the users device screen dimensions
    return window.screen.width * ratio === 1125 && window.screen.height * ratio === 2436
  }
  return false
}

/**
 * @param  {string} [userAgent] navigator userAgent
 * @returns {boolean} true if userAgent matches android strings
 */
export const isAndroid = (userAgent) => {
  const ua = userAgent || getUserAgent()
  return ua ? /android/i.test(ua) : false
}

export const isFirefox = (userAgent) => {
  const ua = userAgent || getUserAgent()
  return ua ? /firefox/i.test(ua) : false
}

export const isSafari = (userAgent) => {
  const ua = userAgent || getUserAgent()
  return ua ? /safari/i.test(ua) && !/chrome/i.test(ua) : false
}

/**
 * detect IE
 * returns version of IE or false, if browser is not Internet Explorer
 * http://stackoverflow.com/questions/19999388/jquery-check-if-user-is-using-ie
 *
 * @param {string} [userAgent]
 */
export const isIE = (userAgent) => {
  const ua = userAgent || getUserAgent()
  if (ua) {
    const msie = ua.indexOf('MSIE ')

    if (msie > 0) {
      // IE 10 or older => return version number
      return Number.parseInt(ua.substring(msie + 5, ua.indexOf('.', msie)), 10)
    }

    const trident = ua.indexOf('Trident/')
    if (trident > 0) {
      // IE 11 => return version number
      const rv = ua.indexOf('rv:')
      return Number.parseInt(ua.substring(rv + 3, ua.indexOf('.', rv)), 10)
    }

    const edge = ua.indexOf('Edge/')
    if (edge > 0) {
      // IE 12 => return version number
      return Number.parseInt(ua.substring(edge + 5, ua.indexOf('.', edge)), 10)
    }
  }
  return false
}

/**
 * Returns the iPhone OS version for a given userAgent or undefined if userAgent
 * does not include an iPhone OS version
 *
 * @param  {string} [userAgent] navigator userAgent
 * @returns {number|undefined} iOSVersion or undefined
 */
export const getIPhoneOSVersion = (userAgent) => {
  const ua = userAgent || getUserAgent()
  const regexp = /iPhone OS \d+_\d+/g
  const iOSVersion = ua.match(regexp)?.[0]?.replace('iPhone OS ', '').replace('_', '.')
  return iOSVersion && Number(iOSVersion)
}

// :::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :::                                                                         :::
// :::  This routine calculates the distance between two points (given the     :::
// :::  latitude/longitude of those points). It is being used to calculate     :::
// :::  the distance between two locations using GeoDataSource (TM) products   :::
// :::                                                                         :::
// :::  Definitions:                                                           :::
// :::    South latitudes are negative, east longitudes are positive           :::
// :::                                                                         :::
// :::  Passed to function:                                                    :::
// :::    lat1, lng1 = Latitude and Longitude of point 1 (in decimal degrees)  :::
// :::    lat2, lng2 = Latitude and Longitude of point 2 (in decimal degrees)  :::
// :::    unit = the unit you desire for results                               :::
// :::           where: 'M' is statute miles (default)                         :::
// :::                  'K' is kilometers                                      :::
// :::                  'N' is nautical miles                                  :::
// :::                                                                         :::
// :::  Worldwide cities and other features databases with latitude longitude  :::
// :::  are available at http://www.geodatasource.com                          :::
// :::                                                                         :::
// :::  For enquiries, please contact sales@geodatasource.com                  :::
// :::                                                                         :::
// :::  Official Web site: http://www.geodatasource.com                        :::
// :::                                                                         :::
// :::               GeoDataSource.com (C) All Rights Reserved 2015            :::
// :::                                                                         :::
// :::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// ref: http://www.geodatasource.com/developers/javascript
export const distance = (
  /** @type {{lat1?: number, lat2?: number, lng1?: number, lng2?: number, unit?: 'K' | 'M' | 'N'}} */ args = {}
) => {
  const { lat1, lng1, lat2, lng2, unit = 'M' } = args
  const radlat1 = (Math.PI * lat1) / 180
  const radlat2 = (Math.PI * lat2) / 180
  const theta = lng1 - lng2
  const radtheta = (Math.PI * theta) / 180
  let dist =
    Math.sin(radlat1) * Math.sin(radlat2) +
    Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta)

  dist = Math.acos(dist)
  dist = (dist * 180) / Math.PI
  dist = dist * 60 * 1.1515
  if (unit === 'K') {
    dist *= 1.609344
  } else if (unit === 'N') {
    dist *= 0.8684
  }
  return dist
}

export const isIframe = () => {
  if (ON_BROWSER && typeof window !== 'undefined') {
    return window.self !== window.top
  }
  return false
}

export const isMobile = () => {
  if (ON_BROWSER && typeof window !== 'undefined') {
    return (
      (isPadMapper && !isCypress() && isIframe()) || window.innerWidth <= GridC.MAX_MOBILE_WIDTH
    )
  }
  return false
}

export const isOneColumnWidth = () => {
  if (ON_BROWSER && typeof window !== 'undefined') {
    return window.matchMedia('(max-width: 1279px)').matches
  }
  return false
}

export const randomNumber = (min, max) => {
  return Math.round(Math.random() * (max - min) + min)
}

/**
 * range(...)
 * range(stop) -> list of integers
 * range(start, stop[, step]) -> list of integers
 *
 * @param start
 * @param stop
 * @param step
 */
export const range = (start, stop, step = 1) => {
  let begin = start
  let end = stop
  if (stop === undefined) {
    end = start
    begin = 0
  }
  const arr = []
  for (; begin < end; begin += step) {
    arr.push(begin)
  }
  return arr
}

/**
 * camel case, camel_case, camel-case => camelCase
 * @param {string | null | undefined | false | 0} str
 * @returns {string}
 */
export const toCamelCase = (str) => {
  if (!str) {
    return ''
  }
  return (
    str
      // trim the leading & trailing snake characters
      .replace(/^[\s_-]+|[\s_-]+$/g, '')
      // change -(t)hing to (T)hing
      .replace(/(?:[\s_-]+)(\w)/g, (match, p1) => p1.toUpperCase())
      // lowercase the first letter
      .replace(/^[A-Z]/, (s) => s.toLowerCase())
  )
}

/**
 * snakeCase, snake case, snake Case -> snake-case
 * @param {string | null | undefined} str
 * @param {boolean} [underScore]
 * @returns {string}
 */
export const toSnakeCase = (str, underScore) => {
  if (!str) {
    return ''
  }
  const snake = str
    // change all runs of space|-|_ to a single -
    .replace(/[\s_-]+/g, '-')
    // change foo(A)Z to foo(-A)Z
    .replace(/([^A-Z-])([A-Z])/g, '$1-$2')
    // change AAA(Z)ee to AAA(-Z)ee
    .replace(/([A-Z])([A-Z])([^A-Z-])/g, '$1-$2$3')
    // strip() leading/trailing hyphens
    .replace(/^[\s_-]+|[\s_-]+$/g, '')
    .toLowerCase()
  return underScore ? snake.replace(/-/g, '_') : snake
}

// normal_string, normal-string, normalString => Normal string
export const toString = (str) => {
  if (!str) {
    return ''
  }

  return (
    str
      // insert a space before all caps
      .replace(/([A-Z])/g, ' $1')
      // replace -, _ and spaces with a single space
      .replace(/[-_\s]+/g, ' ')
      // uppercase the first character
      .replace(/^./, (s) => {
        return s.toUpperCase()
      })
  )
}

export const toTelHref = (numberStr) => {
  if (!numberStr) {
    return 'tel:'
  }
  return `tel:${numberStr.replace('ext.', ',').replace(/[^0-9.,+]/g, '')}`
}

export const updatePageTitle = (title = '') => {
  if (ON_BROWSER && typeof window !== 'undefined') {
    if (title !== window.document.title) {
      window.document.title = title
    }
  }
}

/**
 * approximation of lodash/throttle a "debouncer" of sorts.
 * https://lodash.com/docs/4.17.4#throttle
 * NOTE:
 * - just like lodash if both leading/trailing are === false, this will never call fn()
 * under any sequence of events.
 * - since the throttled function attempts to return previously given return values, it
 * inherently leaks this memory by holding onto that last return value somewhat indefinitely, so
 * don't use with large return values that must be gc'd.
 * - changing options after the throttled function is created is not supported
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn
 * @param {number} wait
 * @param {{ leading?: boolean, trailing?: boolean }} options (leading:true, trailing: true).
 * @returns {((...args: Parameters<T>) => ReturnType<T> | undefined) & { cancel: () => boolean, flush: () => ReturnType<T> | undefined }}
 */
/* eslint-disable no-multi-assign */
export const throttle = (fn, wait, options = {}) => {
  const { leading, trailing } = options
  let self
  let argv
  let result
  let timeout = null
  let previous = 0

  const later = () => {
    const myself = self
    const myargv = argv
    previous = leading === false ? 0 : Date.now()
    self = argv = timeout = null
    result = fn.apply(myself, myargv)
    return result
  }

  /**
   * @param {...any} args
   */
  function throttled(...args) {
    const now = Date.now()
    if (!previous && leading === false) {
      previous = now
    }
    const remaining = wait - (now - previous)
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout)
        timeout = null
      }
      previous = now
      result = fn.apply(this, args)
    } else if (trailing !== false) {
      self = this
      argv = args
      if (!timeout) {
        timeout = setTimeout(later, remaining)
      }
    }
    return result
  }
  throttled.cancel = () => {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
      return true
    }
    return false
  }
  throttled.flush = () => (timeout ? later() : undefined)
  return throttled
}

/**
 * arr.forEach() that no-ops if the array is falsey/empty
 *
 * @param arr
 * @param callback
 */
export const forEach = (arr, callback) => {
  if (arr && arr.length) {
    arr.forEach(callback)
  }
}

/**
 * arr.map() that no-ops if the array is falsey/empty, returns []
 *
 * @param arr
 * @param callback
 */
export const map = (arr, callback) => {
  if (arr && arr.length) {
    return arr.map(callback)
  }
  return []
}

/**
 * FILTERED map over Array (do not accumulate undefined), callback(value, key, arr)
 *
 * @param arr
 * @template T
 * @param callback
 * @template R
 * @param {readonly T[]} arr
 * @param {(item: T, index: number, array: readonly T[]) => R} callback
 * @returns {Array<Exclude<R, undefined>>}
 */
const mapFiltered = (arr, callback) => {
  const output = []
  if (arr && arr.length) {
    arr.forEach((v, i, a) => {
      const r = callback(v, i, a)
      if (r !== undefined) {
        output.push(r)
      }
    })
  }
  return output
}

/**
 * returns an array of the indexes of true values in a boolean array ([false, true, ...] to [1, ..])
 *
 * @param {readonly boolean[] | undefined} boolArr
 * @returns {number[]}
 */
export const boolArrayToIntArray = (boolArr) => {
  return mapFiltered(boolArr, (item, idx) => (item ? idx : undefined))
}

/**
 * some over Object.keys, callback(value, key, obj)
 *
 * @param obj
 * @param callback
 */
export const osome = (obj, callback) => {
  if (obj) {
    return Object.keys(obj).some((k) => callback(obj[k], k, obj))
  }
  return false
}

/* { a: true, b: undefined, c: false, d: true} => [a, d]
 * @param obj
 * @param onlyTruthy filter out falsy values
 * @param formatter string formatter for object key. e.g with toSnakeCase formatter, zuA -> zu-a
 */
export const objToArray = (obj, onlyTruthy, formatter) => {
  if (!obj) {
    return []
  }
  const arr = omap(obj, (val, key) => {
    if (val) {
      return formatter ? formatter(key) : key
    }
    return null
  })

  return onlyTruthy ? arr.filter((v) => v) : arr
}

/**
 * { a: true, b: undefined, c: false, d: true} => [a, d]
 *
 * @param record object
 * @param formatter string formatter for object key. e.g with toSnakeCase formatter, zuA -> zu-a
 */
export const objectToArray = (record, formatter) => {
  if (!record) {
    return []
  }
  const arr = []
  oforEach(record, (val, key) => {
    if (val) {
      arr.push(formatter ? formatter(key) : String(key))
    }
  })
  return arr
}

/**
 *
 * @param {readonly unknown[] | null | undefined | false | 0 | ''} arr
 */
export const arrIsEmpty = (arr) => !arr || !arr.length

/**
 *
 * @template {unknown} T
 * @param {T} obj
 * @returns {obj is T & (null | undefined | false | 0 | 0n | '' | (any[] & {length: 0} | (T extends {} ? { [Key in keyof T]?: undefined } : never)))}
 */
export const oisEmpty = (obj) => !obj || !Object.keys(obj).length

/**
 * Promise.reject cannot be called 'unbound' this simplifies using it as a then/catch target
 *
 * @param e
 */
export const reject = (e) => Promise.reject(e)

/**
 * wrap a promise with a catch that ignores errors, returning the `result` instead
 *
 * @param promise {Promise}
 * @param result
 * @returns {Promise}
 */
export const ignore = (promise, result = null) => promise.catch(() => result)

/**
 * -finally:function- equivalent, adorns a promise with the given function as both then/catch
 *
 * @param promise {Promise}
 * @param always {Function}
 * @returns {Promise}
 */
export const lastly = (promise, always) => promise.then(always, always)

/**
 * -finally:value- equivalent, adorns a promise with a function that will 'lastly' return the value.
 * hence the output is 'invariant' to success or failure.
 *
 * @param promise {Promise}
 * @param result an invariant value
 * @returns {Promise}
 */
export const invariably = (promise, result) => lastly(promise, () => result)

/**
 * return the first argument that is not null/undefined, even if it IS falsey
 */
export function coalesce() {
  /* eslint-disable */
  for (let i = 0, len = arguments.length; i < len; ++i) {
    if (arguments[i] !== undefined && arguments[i] !== null) {
      return arguments[i]
    }
  }
  return undefined
  /* eslint-enable */
}

/**
 * equality check for immutables, checks `===`, hashCode(), then equals()
 * much faster than just equals() for nested objects that differ.
 *
 * @param first
 * @param second
 */
export const eq = (first, second) => {
  // noinspection JSUnresolvedFunction
  if (first === second) {
    return true
  }
  if (!first || !second) {
    return false
  }
  if (
    typeof first.hashCode === 'function' &&
    typeof second.hashCode === 'function' &&
    first.hashCode() !== second.hashCode()
  ) {
    return false
  }
  if (typeof first.equals === 'function') {
    return first.equals(second)
  }
  return false
}

/*
 * Distance function for a specific usage for two objects that has `lat` and `lng` properties.
 */
export const getDistance = ({ lat: lat1, lng: lng1 }, { lat: lat2, lng: lng2 }) => {
  return distance({ lat1, lng1, lat2, lng2 })
}

/**
 * Create a hash from a string.
 * Borrowed from KimKha's answer to http://stackoverflow.com/questions/194846
 *
 * @param {string} str
 * @returns {number}
 */
export const hashCode = (str) => {
  let hash = 0
  let char
  if (str.length === 0) {
    return hash
  }

  for (let i = 0, len = str.length; i < len; i++) {
    char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash |= 0 // Convert to 32bit integer
  }
  return hash
}

/**
 * Ignore a react event
 *
 * @param event
 */
const ignoreEvent = (event) => {
  event && typeof event.preventDefault === 'function' && event.preventDefault()
}

export const shouldOpenBlank = (event) => {
  // Determines if a user ctrl or cmd clicks. Use for opening new tabs
  return event && (event.metaKey || event.ctrlKey)
}

/**
 * Stop propagation of a react event
 *
 * @param {Pick<Event, 'stopPropagation'>} event
 */
export const stopPropagation = (event) => {
  event && typeof event.stopPropagation === 'function' && event.stopPropagation()
}

export const voidEvent = (event) => {
  ignoreEvent(event)
  stopPropagation(event)
}

export const createBindArgToFunc = () => {
  const cache = new WeakMap()

  return (func, arg) => {
    if (!cache.has(func)) {
      const argCache = new Map()
      cache.set(func, argCache)
    }
    const argCache = cache.get(func)
    if (!argCache.has(arg)) {
      argCache.set(arg, () => func(arg))
    }
    return argCache.get(arg)
  }
}

export const getCountSequence = (count) => {
  const sequence = []

  if (isNumber(count)) {
    for (let i = 0; i < count; ++i) {
      sequence.push(i)
    }
  }

  return sequence
}

export const scrollYTo = ({ behavior, element, top }) => {
  const target = element || window
  if (typeof target !== 'undefined' && isNumber(top)) {
    if (target.scrollTo) {
      if (behavior) {
        try {
          target.scrollTo({ behavior, left: 0, top })
          return
        } catch (e) {
          logger.error(
            `${element ? 'element' : 'window'}.scrollTo with scrollToOptions failed: `,
            { behavior, top },
            e
          )
        }
      }

      try {
        target.scrollTo(0, top)
        return
      } catch (e) {
        logger.error(`${element ? 'element' : 'window'}.scrollTo failed: `, top, e)
      }
    }

    if (!element) {
      window.document.documentElement.scrollTop = top
      window.document.body.scrollTop = top
    }
  }
}

/**
 * @param  {string} [userAgent] navigator userAgent
 * @returns {boolean} true if userAgent matches ios, android, or other mobile strings
 */
export const isMobileDevice = (userAgent) => {
  const ua = userAgent || getUserAgent()
  if (ua) {
    return isIOS(ua) || isAndroid(ua) || /webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua)
  }
  return false
}
